import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, PRIMARY_OUTLET, Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import {
  AuthUser,
  Provider,
  SignInWithPasswordCredentials,
  SupabaseClient,
  User,
} from '@supabase/supabase-js';
import { Database } from 'supabase/types/supabase';
import {
  getLoginRoutePath,
  getNonUserRoutePath,
  getOAuthCallbackUrl,
  getUserRoutePath,
  homeRoutePath,
  resetPasswordRoutePath,
} from '../../app.routes';
import { AlertService } from '../alert/alert.service';
import { AppleAuthService } from '../capacitor/apple-auth.service';
import { CapacitorService } from '../capacitor/capacitor.service';
import { GoogleAuthService } from '../capacitor/google-auth.service';
import { LoadingService } from '../loading/loading.service';
import { LoggingService } from '../logging/logging.service';
import { LocalStorageService } from '../storage/local-storage.service';
import { UserService } from '../user/user.service';
import { WindowService } from '../window/window.service';

// Key for storing the last viewed user ID in localStorage
export const LAST_USER_ID_KEY = 'lastViewedUserId' as const;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private activatedRoute = inject(ActivatedRoute);
  private alertService = inject(AlertService);
  private appleAuthService = inject(AppleAuthService);
  private capacitorService = inject(CapacitorService);
  private googleAuthService = inject(GoogleAuthService);
  private loadingService = inject(LoadingService);
  private localStorage = inject(LocalStorageService);
  private logger = inject(LoggingService);
  private router = inject(Router);
  private navCtrl = inject(NavController);
  private supabaseClient = inject<SupabaseClient<Database>>(SupabaseClient);
  private userService = inject(UserService);
  private windowService = inject(WindowService);

  private readonly loginRouterLink = [getLoginRoutePath()];

  private loginNavigation: Promise<unknown> | undefined;

  constructor() {
    this.supabaseClient.auth.onAuthStateChange((event, session) => {
      // Prevent supabase deadlocks with setTimeout
      // See https://supabase.com/docs/reference/javascript/auth-signup#auth-onauthstatechange
      setTimeout(async () => {
        if (event === 'SIGNED_OUT') {
          await this.navigateToLogin();
          return;
        }

        if (event === 'PASSWORD_RECOVERY') {
          await this.router.navigate([
            getNonUserRoutePath(resetPasswordRoutePath),
          ]);
          return;
        }
      }, 0);
    });
  }

  async deleteAccount() {
    try {
      this.loadingService.setLoading(true);

      const { error } = await this.supabaseClient.rpc('delete_user');

      if (error) {
        throw error;
      }

      this.loadingService.setLoading(false);

      await this.navigateToLogin();

      await this.alertService.createNoticeWithOptions({
        message: 'Account deleted successfully',
        duration: 5000,
        position: 'bottom',
      });
    } catch (error: unknown) {
      const message = (error as Error).message || 'An error occurred';
      await this.alertService.createNoticeWithOptions({
        message,
        duration: 5000,
        position: 'bottom',
        color: 'danger',
      });
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  async forgotPassword(email: string) {
    try {
      this.loadingService.setLoading(true);

      const { error } = await this.supabaseClient.auth.resetPasswordForEmail(
        email,
        {
          redirectTo: `${this.windowService.getOrigin()}/${resetPasswordRoutePath}`,
        },
      );

      if (error) {
        throw error;
      }

      await this.alertService.createNoticeWithOptions({
        message: 'Password reset email sent! Please check your inbox.',
        duration: 5000,
        position: 'bottom',
      });
    } catch (error: unknown) {
      const message = (error as Error).message || 'An error occurred';
      await this.alertService.createNoticeWithOptions({
        message,
        duration: 5000,
        position: 'bottom',
      });
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  async getAuthUser() {
    try {
      const { data, error } = await this.supabaseClient.auth.getClaims();

      if (error) {
        this.logger.error('Error getting auth user:', error);
        return null;
      }

      const claims = data?.claims;
      if (!claims) {
        return null;
      }

      // Extract user information from JWT claims
      return {
        id: claims.sub, // User ID is in the 'sub' claim
        email: claims['email'],
        role: claims.role,
        aud: claims.aud,
        app_metadata: claims['app_metadata'] || {},
        user_metadata: claims['user_metadata'] || {},
      } as AuthUser;
    } catch (error) {
      this.logger.error('Exception getting auth user:', error);
      return null;
    }
  }

  getLastSelectedUserId(): string | null {
    try {
      const result = this.localStorage.getItem(LAST_USER_ID_KEY);
      return typeof result === 'string' ? result : null;
    } catch (error) {
      this.logger.error('Error getting last selected user ID:', error);
      return null;
    }
  }

  async getSessionUser() {
    const { data, error } = await this.supabaseClient.auth.getSession();

    if (error) {
      throw error;
    }

    return data.session?.user;
  }

  async initializeAppForUser(userId: string, redirect?: string | null) {
    this.saveLastSelectedUserId(userId);
    const startUrl = this.getCurrentUrlForNewUser(userId);
    const redirectUrl = redirect ? decodeURIComponent(redirect) : startUrl;

    // Reinitialize the router, and push an entry to the stack so that the back button works
    await this.navCtrl.navigateRoot(redirectUrl, { animated: true });
  }

  async signInWithApple(): Promise<any> {
    try {
      if (!this.capacitorService.web && !this.capacitorService.ios) {
        throw new Error(
          'Sign in with Apple is only available on iOS and web browsers.',
        );
      }

      if (this.capacitorService.web) {
        return this.signInWithOAuth('apple');
      }

      let response: { data: { user: User } };
      try {
        response = await this.signInWithAppleIOS();
      } catch (error: unknown) {
        if (this.userCanceledSignInWithApple(error as Error)) {
          return;
        }

        throw error;
      }

      const user = response.data.user;
      if (!user) {
        throw new Error('User not found');
      }

      const redirectParam =
        this.activatedRoute.snapshot.queryParamMap.get('redirect');
      await this.initializeAppForUser(user.id, redirectParam);
    } catch (error: unknown) {
      // Footer anchor not available before login
      const message = (error as Error).message || 'An error occurred';
      await this.alertService.createNoticeWithOptions({
        message,
        duration: 5000,
        position: 'bottom',
      });
    }
  }

  async signInWithGoogle(): Promise<any> {
    try {
      if (!this.capacitorService.mobile) {
        return this.signInWithOAuth('google');
      }

      let response: { data: { user: User } };
      try {
        response = await this.signInWithGoogleNative();
      } catch (error: unknown) {
        if (this.userCanceledSignInWithGoogle(error as Error)) {
          return;
        }

        throw error;
      }

      const user = response.data?.user;
      if (!user) {
        throw new Error('User not found');
      }

      const redirectParam =
        this.activatedRoute.snapshot.queryParamMap.get('redirect');
      await this.initializeAppForUser(user.id, redirectParam);
    } catch (error: unknown) {
      // Footer anchor not available before login
      const message = (error as Error).message || 'An error occurred';
      await this.alertService.createNoticeWithOptions({
        message,
        duration: 5000,
        position: 'bottom',
      });
    }
  }

  async signIn(credentials: SignInWithPasswordCredentials) {
    try {
      this.loadingService.setLoading(true);

      const { error } =
        await this.supabaseClient.auth.signInWithPassword(credentials);

      if (error) {
        throw error;
      }

      // Navigate to user-specific route
      const user = await this.getSessionUser();
      if (!user) {
        throw new Error('User not found');
      }

      const redirectParam =
        this.activatedRoute.snapshot.queryParamMap.get('redirect');
      await this.initializeAppForUser(user.id, redirectParam);
    } catch (error: unknown) {
      // Footer anchor not available before login
      const message = (error as Error).message || 'An error occurred';
      await this.alertService.createNoticeWithOptions({
        message,
        duration: 5000,
        position: 'bottom',
      });
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  async signOut() {
    const response = await this.supabaseClient.auth.signOut();

    // Navigate here as well as from the SIGNED_OUT handler: the event is the
    // only signal for a session that ends on its own, and this call is the only
    // one that still runs if signing out fails to emit one.
    await this.navigateToLogin();

    return response;
  }

  async signUp(credentials: SignInWithPasswordCredentials) {
    try {
      const { data, error } =
        await this.supabaseClient.auth.signUp(credentials);

      if (error) {
        throw error;
      }

      // Navigate to user-specific route
      const user = data.user;
      if (!user) {
        throw new Error('User not found');
      }

      // Get redirect parameter from query params (including invitation token)
      const redirectParam =
        this.activatedRoute.snapshot.queryParamMap.get('redirect');

      await this.initializeAppForUser(user.id, redirectParam);
    } catch (error: unknown) {
      // Footer anchor not available before login
      const message = (error as Error).message || 'An error occurred';
      await this.alertService.createNoticeWithOptions({
        message,
        duration: 5000,
        position: 'bottom',
      });
    }
  }

  saveLastSelectedUserId(userId: string): void {
    this.localStorage.setItem(LAST_USER_ID_KEY, userId);
  }

  async setSession({
    access_token,
    refresh_token,
  }: {
    access_token: string;
    refresh_token: string;
  }) {
    return this.supabaseClient.auth.setSession({
      access_token,
      refresh_token,
    });
  }

  async updatePassword(password: string) {
    try {
      this.loadingService.setLoading(true);

      const user = await this.getSessionUser();

      if (!user) {
        throw new Error('User not found');
      }

      const { error } = await this.supabaseClient.auth.updateUser({
        password: password,
      });

      if (error) {
        throw error;
      }

      await this.alertService.createNoticeWithOptions({
        message: 'Password updated successfully!',
        duration: 5000,
        position: 'bottom',
      });

      await this.router.navigate([getUserRoutePath(user.id, homeRoutePath)], {
        replaceUrl: true,
      });
    } catch (error: unknown) {
      const message = (error as Error).message || 'An error occurred';
      await this.alertService.createNoticeWithOptions({
        message,
        duration: 5000,
        position: 'bottom',
      });
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  /**
   * Leaves the app through a root navigation, not a push: Ionic keeps departed
   * pages mounted, and a layout left behind that way keeps its side menu
   * registered with the menu controller. `menuController` hands `ion-menu-toggle`
   * the first menu registered, so the next account to sign in would toggle the
   * previous account's menu — invisibly, since its page is hidden — until the
   * app restarted.
   *
   * Sign-out reaches here from the caller and from the SIGNED_OUT event both, so
   * the two share one in-flight navigation rather than racing each other.
   */
  private navigateToLogin() {
    this.loginNavigation ??= Promise.resolve(
      this.navCtrl.navigateRoot(this.loginRouterLink),
    ).finally(() => (this.loginNavigation = undefined));

    return this.loginNavigation;
  }

  // TODO BG do we need this?
  private getCurrentUrlForNewUser(userId: string) {
    // Get the current URL to preserve any query params
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    const nonUserSegments = urlTree.root.children[
      PRIMARY_OUTLET
    ]?.segments.slice(2) || [homeRoutePath];
    const startRoute = nonUserSegments.join('/');
    const queryParams = urlTree.queryParams;
    const queryString =
      Object.keys(queryParams).length > 0
        ? '?' + new URLSearchParams(queryParams).toString()
        : '';
    const startRouteWithParams = startRoute + queryString;
    return getUserRoutePath(userId, startRouteWithParams);
  }

  private async signInWithOAuth(provider: Provider) {
    const origin = this.windowService.getOrigin();
    const redirectParam =
      this.activatedRoute.snapshot.queryParamMap.get('redirect');
    const redirectUrl = redirectParam ? decodeURIComponent(redirectParam) : '';
    const redirectTo = getOAuthCallbackUrl(origin, redirectUrl);
    return this.supabaseClient.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo,
      },
    });
  }

  private async signInWithAppleIOS() {
    const appleResponse = await this.appleAuthService.signInWithApple();
    const provider = 'apple';
    const token = appleResponse.result.idToken;

    if (!token) {
      throw new Error('Missing Apple token');
    }

    const response = await this.supabaseClient.auth.signInWithIdToken({
      provider,
      token,
    });

    const { error } = response;

    if (error) {
      throw error;
    }

    // Apple provides the user's name only on the first sign-in
    const profile = appleResponse.result.profile;
    const fullName = [profile?.givenName, profile?.familyName]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(' ');
    if (fullName && response.data.user) {
      try {
        await this.userService.updateUser(response.data.user.id, {
          full_name: fullName,
        });
      } catch (error) {
        // Don't block sign-in; the welcome page prompts for the name instead
        this.logger.error('Error saving Apple full name:', error);
      }
    }

    return response;
  }

  private async signInWithGoogleNative() {
    const user = await this.googleAuthService.signInWithGoogle();
    const provider = 'google';

    if (user.result.responseType === 'offline') {
      throw new Error('Google offline login not supported');
    }

    const token = user.result.idToken;
    const access_token = user.result.accessToken?.token;

    if (!token || !access_token) {
      throw new Error('Missing Google token or access token');
    }

    const response = await this.supabaseClient.auth.signInWithIdToken({
      provider,
      token,
      access_token,
    });

    const { error } = response;

    if (error) {
      throw error;
    }

    return response;
  }

  private userCanceledSignInWithApple(error: Error) {
    return error?.message?.toLowerCase().includes('error 1001') ?? false;
  }

  private userCanceledSignInWithGoogle(error: Error) {
    return error?.message?.toLowerCase().includes('user canceled') ?? false;
  }
}
